/**
 * FeatureManager — shared style tokens + small helpers.
 *
 * Pulls the window's chrome onto theme tokens (zero styled-components). Each
 * export is a plain `React.CSSProperties` object or pure helper so it spreads
 * straight into inline styles. Behavior-preserving: values map 1:1 onto the
 * pre-existing palette via the semantic theme tokens.
 */
import type { CSSProperties } from 'react';
import {
  COLORS,
  SPACE,
  RADII,
  FONT_SIZE,
  FONT_WEIGHT,
  SHADOW,
  Z,
  panelSurface,
} from '../../theme';
import { FeatureMode } from '../../../../core/features';

/** Map a feature mode to its semantic status colour for indicator dots/badges. */
export function modeColor(mode: FeatureMode): string {
  switch (mode) {
    case FeatureMode.NORMAL: return COLORS.success;
    case FeatureMode.WHITELIST: return COLORS.warning;
    case FeatureMode.DISABLED: return COLORS.danger;
    default: return COLORS.textMuted;
  }
}

/** Fixed-position modal shell (centred, no external scrim — matches legacy). */
export const containerStyle: CSSProperties = {
  ...panelSurface(),
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 900,
  maxWidth: '95vw',
  height: 700,
  maxHeight: '90vh',
  borderRadius: RADII.xl,
  boxShadow: SHADOW.lg,
  display: 'flex',
  flexDirection: 'column',
  color: COLORS.textSecondary,
  zIndex: Z.launcher,
  pointerEvents: 'auto',
};

export const headerStyle: CSSProperties = {
  padding: `${SPACE.xxl}px ${SPACE.xxxl}px`,
  borderBottom: `1px solid ${COLORS.borderSubtle}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

export const titleStyle: CSSProperties = {
  fontSize: FONT_SIZE.xxl,
  fontWeight: FONT_WEIGHT.semibold,
  color: COLORS.textPrimary,
  display: 'flex',
  alignItems: 'center',
  gap: SPACE.md,
};

export const subtitleStyle: CSSProperties = {
  fontSize: FONT_SIZE.md,
  color: COLORS.textMuted,
  marginTop: SPACE.xs,
};

export const layoutStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
};

export const contentStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

export const featureListStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: `${SPACE.xl}px ${SPACE.xxl}px`,
};

export const groupTitleStyle: CSSProperties = {
  fontSize: FONT_SIZE.md,
  fontWeight: FONT_WEIGHT.semibold,
  color: COLORS.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: SPACE.lg,
  paddingLeft: SPACE.xs,
};

export const emptyStateStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: COLORS.textMuted,
  textAlign: 'center',
  padding: SPACE.xxxl * 2,
};
